import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hoe lang er gewacht wordt na de laatste wijziging voor er geschreven wordt.
 * Lang genoeg om een woord uit te typen, kort genoeg om niet als vertraging te
 * voelen.
 */
const SAVE_DELAY_MS = 800

/**
 * @typedef {'idle' | 'saving' | 'saved' | 'error'} DebouncedWriteStatus
 */

/**
 * Wacht tot de gebruiker even stopt en schrijft dan één keer, met een
 * schrijffunctie naar keuze. De wacht-en-schrijf-logica van `useAutosaveField`,
 * losgemaakt van Firestore: een inzicht mag alleen via een Cloud Function
 * geschreven worden, en die kaart heeft precies dezelfde bouwsteen nodig.
 *
 * Twee dingen horen bij elkaar en zitten hier allebei in:
 * 1. **Wachten tot de gebruiker even stopt** — honderden aanslagen worden één
 *    schrijfactie.
 * 2. **Wegschrijven bij het verlaten van de pagina** — anders verplaatst de
 *    wachttijd het gat alleen maar: wie binnen de wachttijd wegklikt is zijn
 *    laatste zin kwijt. `pagehide` dekt het sluiten van de tab en een harde
 *    herlaad; de opruimfunctie dekt wegnavigeren binnen de app.
 * AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Elke sleutel heeft zijn eigen wachttijd, zodat typen in het ene veld een
 * wachtend ander veld niet afbreekt. `status` is bedoeld om bij het veld te
 * zetten en blijft staan: 'saved' verdwijnt pas als er weer iets te bewaren
 * valt. Mislukt een schrijfactie, dan is dat 'error' mét de reden — 'saved'
 * tonen zou een onwaarheid zijn.
 *
 * @template P
 * @param {(key: string, payload: P) => (Promise<unknown> | unknown)} write - de schrijfactie; mag gooien of een afgewezen belofte teruggeven
 * @param {number} [delay] - wachttijd in ms na de laatste wijziging
 * @returns {{save: (key: string, payload: P) => void, flush: () => Promise<void>, status: DebouncedWriteStatus, error: string}}
 */
const useDebouncedWrite = (write, delay = SAVE_DELAY_MS) => {
    /** @type {[DebouncedWriteStatus, Function]} */
    const [status, setStatus] = useState('idle')
    const [error, setError] = useState('')

    /** Hoeveel schrijfacties er op dit moment onderweg zijn. */
    const inFlight = useRef(0)

    /**
     * Wat er nog geschreven moet worden, per sleutel.
     * @type {React.MutableRefObject<Map<string, {payload: P, timer: number}>>}
     */
    const pending = useRef(new Map())

    // De nieuwste schrijffunctie, zodat een aanroeper geen useCallback hoeft te
    // doen en een wachtende schrijfactie niet met een verouderde sluiting
    // schrijft.
    const writeRef = useRef(write)
    useEffect(() => { writeRef.current = write }, [write])

    const mounted = useRef(true)
    useEffect(() => () => { mounted.current = false }, [])

    const flushKey = useCallback((key) => {
        const entry = pending.current.get(key)
        if (!entry) return Promise.resolve()

        pending.current.delete(key)
        clearTimeout(entry.timer)
        inFlight.current += 1

        // Synchroon aanroepen: bij het verlaten van de pagina is er geen
        // volgende tik meer waarin een uitgestelde aanroep nog zou draaien.
        let belofte
        try {
            belofte = Promise.resolve(writeRef.current(key, entry.payload))
        } catch (err) {
            belofte = Promise.reject(err)
        }
        return belofte.then(() => {
            inFlight.current -= 1
            // Pas 'saved' zeggen als er niets meer wacht én niets meer
            // onderweg is; anders zou het scherm 'bewaard' claimen terwijl
            // een tweede veld nog in de rij staat.
            if (mounted.current && inFlight.current === 0 && pending.current.size === 0) setStatus('saved')
        }, (err) => {
            inFlight.current -= 1
            if (mounted.current) {
                setError(err && err.message ? err.message : 'Opslaan mislukt')
                setStatus('error')
            }
        })
    }, [])

    const save = useCallback((key, payload) => {
        setStatus('saving')
        setError('')
        const existing = pending.current.get(key)
        if (existing) clearTimeout(existing.timer)
        const timer = setTimeout(() => flushKey(key), delay)
        pending.current.set(key, { payload, timer })
    }, [delay, flushKey])

    /** Alles wat wacht nu wegschrijven; wacht tot het klaar is. */
    const flush = useCallback(
        () => Promise.all(Array.from(pending.current.keys()).map(flushKey)).then(() => undefined),
        [flushKey],
    )

    useEffect(() => {
        const flushAll = () => { flush() }
        window.addEventListener('pagehide', flushAll)
        return () => {
            window.removeEventListener('pagehide', flushAll)
            flushAll()
        }
    }, [flush])

    return { save, flush, status, error }
}

export { SAVE_DELAY_MS }
export default useDebouncedWrite
